import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from "react";
import {
  anchorFromPanelPosition,
  panelPositionFromAnchor,
} from "./dockGeometry";
import { useFloatingBox } from "./useFloatingBox";

/** Docked card geometry before any resize. Expanded and mobile override both. */
const DEFAULT_WIDTH_PX = 384;
const DEFAULT_HEIGHT_PX = 600;
/** Roomy enough to read a thread side by side with the page behind it. */
const MAX_WIDTH_PX = 1000;

/**
 * Every resize handle: four edges and four corners, as compass directions.
 * `cursor` is the one the browser shows; `box` positions the hit area, which is
 * deliberately far larger than the visible line — a hairline strip is a target
 * most people never manage to hit.
 */
const RESIZE_HANDLES = [
  { edge: "n", cursor: "cursor-ns-resize", box: "inset-x-5 -top-1.5 h-3" },
  { edge: "s", cursor: "cursor-ns-resize", box: "inset-x-5 -bottom-1.5 h-3" },
  { edge: "w", cursor: "cursor-ew-resize", box: "inset-y-5 -left-1.5 w-3" },
  { edge: "e", cursor: "cursor-ew-resize", box: "inset-y-5 -right-1.5 w-3" },
  { edge: "nw", cursor: "cursor-nwse-resize", box: "-top-1.5 -left-1.5 h-5 w-5" },
  { edge: "ne", cursor: "cursor-nesw-resize", box: "-top-1.5 -right-1.5 h-5 w-5" },
  { edge: "sw", cursor: "cursor-nesw-resize", box: "-bottom-1.5 -left-1.5 h-5 w-5" },
  { edge: "se", cursor: "cursor-nwse-resize", box: "-bottom-1.5 -right-1.5 h-5 w-5" },
];

/** Which edges each handle paints, so the hover hint matches what will move. */
const EDGE_BARS = {
  n: ["top"],
  s: ["bottom"],
  w: ["left"],
  e: ["right"],
  nw: ["top", "left"],
  ne: ["top", "right"],
  sw: ["bottom", "left"],
  se: ["bottom", "right"],
};

const BAR_CLASS = {
  top: "absolute inset-x-0 top-1/2 h-1 -translate-y-1/2 rounded-full",
  bottom: "absolute inset-x-0 top-1/2 h-1 -translate-y-1/2 rounded-full",
  left: "absolute inset-y-0 left-1/2 w-1 -translate-x-1/2 rounded-full",
  right: "absolute inset-y-0 left-1/2 w-1 -translate-x-1/2 rounded-full",
};

/**
 * Lets `PanelHeader` pick up the move gesture without the panel having to own
 * its own header. The header is the natural grab area — it is the one strip
 * guaranteed to exist and to have no scrolling content — but it is passed in as
 * a child, so the props have to travel down rather than across.
 */
const FloatingPanelContext = createContext({
  dragHandleProps: {},
  isMoving: false,
  canMove: false,
});

export const useFloatingPanelDrag = () => useContext(FloatingPanelContext);

/**
 * The floating surface a dock opens into: geometry for the three layouts
 * (docked card, expanded overlay, full-screen mobile), drag-to-move by the
 * header, drag-to-resize from any edge or corner, the entrance animation, and
 * the elevated surface treatment. Contents are entirely the caller's.
 *
 * Generic on purpose — pairs with `FloatingBubble` for any floating surface,
 * not just chat.
 *
 * Position and size are one piece of state (`useFloatingBox`) because resizing
 * the top or left edge has to move the box and change its size together. Both
 * persist, so the panel is placed once rather than every session.
 *
 * Layered shadow rather than a single `shadow-2xl`: a tight dark shadow reads
 * as contact with the page while a wide soft one carries the elevation, which
 * is what stops the panel looking like a flat rectangle pasted on top.
 *
 * @param {Object} props
 * @param {string} props.label - Accessible name for the region.
 * @param {boolean} [props.expanded=false]
 * @param {boolean} [props.isMobile=false]
 * @param {string} props.storageKey - Prefix for the persisted size.
 * @param {string} props.anchorKey - The dock's shared position anchor. Pass the
 *   same key to `FloatingBubble` so the launcher and the panel stay aligned.
 * @param {() => void} [props.onEscape] - Escape handler, matching `Drawer`.
 * @param {React.ReactNode} props.children
 */
export function FloatingPanel({
  label,
  expanded = false,
  isMobile = false,
  storageKey,
  anchorKey,
  onEscape,
  children,
}) {
  // A phone can't host a floating card, and an expanded panel already fills the
  // screen — in both there is nowhere to move or resize into.
  const isDocked = !isMobile && !expanded;

  // The launcher and this panel share one stored anchor, so opening the dock
  // puts the panel where the launcher was and closing it leaves the launcher
  // where the panel ended up.
  const fromAnchor = useCallback(
    (anchor, width, height) => panelPositionFromAnchor(anchor, width, height),
    []
  );
  const toAnchor = useCallback(
    (box) => anchorFromPanelPosition(box, box.width, box.height),
    []
  );

  const frame = useFloatingBox({
    anchorKey,
    sizeKey: storageKey,
    defaultWidth: DEFAULT_WIDTH_PX,
    defaultHeight: DEFAULT_HEIGHT_PX,
    maxWidth: MAX_WIDTH_PX,
    toAnchor,
    fromAnchor,
    disabled: !isDocked,
  });

  // Escape dismisses, the same way `common/comms/Drawer` behaves — a floating
  // surface people open dozens of times a day needs a keyboard way out.
  // Deliberately no focus trap and no body scroll lock: unlike a modal, the
  // point of this panel is working alongside the page behind it.
  useEffect(() => {
    if (!onEscape) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") onEscape();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onEscape]);

  const dragContext = useMemo(
    () => ({
      dragHandleProps: frame.moveHandleProps,
      isMoving: frame.isBusy,
      canMove: isDocked,
    }),
    [isDocked, frame.moveHandleProps, frame.isBusy]
  );

  // On a phone the panel is a bottom sheet rather than a full-screen takeover:
  // leaving the conversation visible above it is what makes it read as a layer
  // over the app instead of a separate screen. Expanding lifts the top edge
  // close to the status bar without ever reaching it, so the sheet always looks
  // like something that can be pushed back down.
  const frameClass = isMobile
    ? expanded
      ? "fixed inset-x-0 bottom-0 top-16 z-40"
      : "fixed inset-x-0 bottom-0 top-[32%] z-40"
    : expanded
    ? "fixed inset-0 z-40 m-auto h-[88vh] w-[min(1120px,94vw)]"
    : "fixed z-40";

  const frameStyle = isDocked
    ? {
        left: `${frame.left}px`,
        top: `${frame.top}px`,
        width: `${frame.width}px`,
        height: `${frame.height}px`,
      }
    : undefined;

  return (
    <FloatingPanelContext.Provider value={dragContext}>
      {/* Scrim, mobile only: it deepens as the sheet rises, so how much of the
          app is dimmed tracks how much of it the sheet is covering. Not a modal
          backdrop — it does not capture clicks, matching the panel's own
          no-focus-trap stance. */}
      {isMobile && (
        <div
          aria-hidden="true"
          className={`pointer-events-none fixed inset-0 z-30 bg-[#1A1A18] transition-opacity duration-200 motion-reduce:transition-none ${
            expanded ? "opacity-50" : "opacity-30"
          }`}
        />
      )}

      <section aria-label={label} className={frameClass} style={frameStyle}>
        <div
          className={`animate-dockIn flex h-full flex-col overflow-hidden border border-black/5 bg-white shadow-[0_2px_8px_-2px_rgba(0,0,0,0.12),0_24px_64px_-16px_rgba(15,23,42,0.35)] ${
            isMobile ? "rounded-t-[22px]" : "rounded-2xl"
          }`}
        >
          {/* Grab handle: the affordance that says this surface moves, standing
              in for the drag grip the desktop header carries. */}
          {isMobile && (
            <div className="flex flex-shrink-0 justify-center pb-0.5 pt-2">
              <span aria-hidden="true" className="h-1 w-[38px] rounded-full bg-[#D9D9D9]" />
            </div>
          )}
          {children}
        </div>

        {/* Handles sit outside the rounded, overflow-hidden card so they aren't
            clipped, and after it in the DOM so they paint on top. */}
        {isDocked &&
          RESIZE_HANDLES.map(({ edge, cursor, box }) => (
            <div
              key={edge}
              {...frame.resizeHandleProps(edge)}
              className={`group absolute ${box} ${cursor} touch-none select-none`}
            >
              {EDGE_BARS[edge].map((side) => (
                <span
                  key={side}
                  aria-hidden="true"
                  className={`${BAR_CLASS[side]} transition-colors ${
                    frame.isBusy
                      ? "bg-orange-primary"
                      : "bg-transparent group-hover:bg-orange-primary/50"
                  }`}
                />
              ))}
            </div>
          ))}

        {/* The bottom-right corner keeps a permanent grip: it is the affordance
            that tells people the panel resizes at all. The other seven only
            appear on hover, so the card stays clean. */}
        {isDocked && (
          <span
            aria-hidden="true"
            className={`pointer-events-none absolute bottom-1 right-1 h-2.5 w-2.5 rounded-br-[3px] border-b-2 border-r-2 transition-colors ${
              frame.isBusy ? "border-orange-primary" : "border-gray-300"
            }`}
          />
        )}
      </section>
    </FloatingPanelContext.Provider>
  );
}

export default FloatingPanel;
